import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';

import PopupV2 from '../../../../Popup/PopupV2';
import { getApiAxiosInstance } from '../../../../../utils/network';
import { showErrorPopup } from '../../../../../actions/notification-actions';
import { CppPlayerActions } from '../../../../Analytics/constants';
import useCPPTracking from '../../../../hooks/useCPPTracking';
import {
    ALLOWED_VIDEO_HOSTS,
    DESCRIPTION_MAX_LENGTH,
    MAX_VIDEOS,
    RECRUITING_VIDEOS_ENDPOINT,
    RECRUITING_VIDEOS_ORDER_ENDPOINT,
    TITLE_MAX_LENGTH,
    URL_PLACEHOLDER,
} from '../constants';
import { resolveVideoHost } from '../utils';
import styles from '../../collegeProspectTab.scss';

const TESTID_PREFIX = 'modal.recruiting-videos';
const DROPPABLE_ID = 'recruiting-videos-list';

const buildEmptyDraft = (tempIdSeed) => ({
    tempId: `new-${tempIdSeed}`,
    id: null,
    title: '',
    description: '',
    url: '',
});

const toDrafts = (videos) => videos.map((v) => ({
    tempId: `saved-${v.id}`,
    id: v.id,
    title: v.title || '',
    description: v.description || '',
    url: v.url || '',
}));

const isDraftChanged = (draft, original) => (
    draft.title !== original.title ||
    draft.description !== original.description ||
    draft.url !== original.url
);

const isAllowedVideoUrl = (raw) => {
    const trimmed = raw.trim();
    // Reject malformed protocols on the raw string before `new URL` gets
    // a chance to normalize them. Chrome's URL parser silently rewrites
    // `https:/x`, `https:///x`, `https:////x` -> `https://x` for special
    // schemes, so any non-canonical slash count would otherwise parse to
    // a valid-looking URL and slip through. Require exactly two slashes
    // followed by a non-slash authority character.
    if (!/^https?:\/\/[^/]/i.test(trimmed)) {
        return false;
    }
    try {
        const parsed = new URL(trimmed);
        const host = parsed.hostname.toLowerCase();
        // Exact match only — no endsWith. `.youtube.com` suffix matching would
        // accept `ww.youtube.com` (typo) and any other subdomain a user pastes.
        return ALLOWED_VIDEO_HOSTS.includes(host);
    } catch {
        return false;
    }
};

const getRowFieldErrors = (draft) => {
    const hasTitle = draft.title.trim().length > 0;
    const hasUrl = draft.url.trim().length > 0;
    const hasDescription = draft.description.trim().length > 0;
    if (!hasTitle && !hasUrl && !hasDescription) {
        return {};
    }
    const errors = {};
    if (!hasTitle) {
        errors.title = 'Title is required.';
    } else if (draft.title.length > TITLE_MAX_LENGTH) {
        // Belt-and-suspenders: the <input maxLength> stops typing past the cap,
        // but pasted content or pre-loaded rows over the limit would otherwise
        // hit the server and get rejected mid-batch — which is the partial-
        // save class of bug QA reported. Blocking here means SAVE never gets
        // clicked while a row is knowingly invalid.
        errors.title = `Title must be ${TITLE_MAX_LENGTH} characters or fewer.`;
    }
    if (!hasUrl) {
        errors.url = 'URL is required.';
    } else if (!isAllowedVideoUrl(draft.url)) {
        errors.url = 'Enter a valid YouTube, Vimeo, or Hudl link.';
    }
    if (hasDescription && draft.description.length > DESCRIPTION_MAX_LENGTH) {
        errors.description = `Description must be ${DESCRIPTION_MAX_LENGTH} characters or fewer.`;
    }
    return errors;
};

const hasFieldError = (fieldErrors) => Object.keys(fieldErrors).length > 0;

// Which per-field errors are safe to render inline *before* the player has
// clicked SAVE. Fields with content that fails a format check surface
// immediately (URL that fails the host allowlist, title/description over the
// cap) so the player can fix in place — matches the reference where the URL
// error appears the moment an invalid URL is typed. Empty required fields
// stay quiet until save attempt so the modal doesn't scold on mount.
const getPreAttemptVisibleErrors = (draft, rowErrors) => {
    const visible = {};
    if (draft.title.trim().length > 0 && rowErrors.title) {
        visible.title = rowErrors.title;
    }
    if (draft.description.trim().length > 0 && rowErrors.description) {
        visible.description = rowErrors.description;
    }
    if (draft.url.trim().length > 0 && rowErrors.url) {
        visible.url = rowErrors.url;
    }
    return visible;
};

// Presence-only check: is a row missing one of its required fields? Coarser
// than getRowFieldErrors (which also flags a bad host on a non-empty URL) —
// used to gate whether SAVE is even eligible to light up. URL validity is
// deferred to click-time so a user typing an in-progress URL still sees the
// button as clickable.
const isRowContentIncomplete = (draft) => {
    const hasTitle = draft.title.trim().length > 0;
    const hasUrl = draft.url.trim().length > 0;
    const hasDescription = draft.description.trim().length > 0;
    if (!hasTitle && !hasUrl && !hasDescription) {
        return false;
    }
    return !hasTitle || !hasUrl;
};

const RecruitingVideosModal = ({ initialVideos, onClose, onSaved }) => {
    const dispatch = useDispatch();
    const { trackPlayerEngagement } = useCPPTracking();
    const tempIdSeed = useRef(0);
    const nextTempId = () => {
        tempIdSeed.current += 1;
        return tempIdSeed.current;
    };

    const seededDrafts = useMemo(() => {
        if (initialVideos.length === 0) {
            return [buildEmptyDraft(0)];
        }
        return toDrafts(initialVideos);
    }, [initialVideos]);

    const [drafts, setDrafts] = useState(seededDrafts);
    const [isSaving, setIsSaving] = useState(false);
    // Errors stay hidden until the coach explicitly tries to submit. See the
    // SAVE button below — it stays clickable in its dim state so the click
    // trips this flag and reveals the per-field messages inline.
    const [hasAttemptedSave, setHasAttemptedSave] = useState(false);
    const scrollableRef = useRef(null);
    const previousDraftCountRef = useRef(seededDrafts.length);
    // Ref guards so the open/dismiss events fire once per mount even if the
    // effect re-runs or the parent re-renders through the same modal instance.
    const hasFiredOpenEventRef = useRef(false);
    const hasFiredDismissEventRef = useRef(false);

    useEffect(() => {
        if (hasFiredOpenEventRef.current) {
            return;
        }
        hasFiredOpenEventRef.current = true;
        trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEOS_MODAL_OPENED, {
            initial_count: initialVideos.length,
        });
    // trackPlayerEngagement is re-created every render — key on initialVideos so the
    // fire-once guard is what actually gates it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialVideos]);

    useEffect(() => {
        const node = scrollableRef.current;
        if (drafts.length > previousDraftCountRef.current && node) {
            if (typeof node.scrollTo === 'function') {
                node.scrollTo({ top: node.scrollHeight, behavior: 'smooth' });
            } else {
                node.scrollTop = node.scrollHeight;
            }
        }
        previousDraftCountRef.current = drafts.length;
    }, [drafts.length]);

    const originalById = useMemo(() => {
        const map = new Map();
        initialVideos.forEach((v) => {
            map.set(v.id, { title: v.title || '', description: v.description || '', url: v.url || '' });
        });
        return map;
    }, [initialVideos]);

    const canAddMore = drafts.length < MAX_VIDEOS;
    const isReorderable = drafts.length > 1;

    const updateField = (tempId, field, value) => {
        setDrafts((prev) => prev.map((d) => (d.tempId === tempId ? { ...d, [field]: value } : d)));
    };

    const handleAddVideo = () => {
        if (!canAddMore) {
            return;
        }
        setDrafts((prev) => [...prev, buildEmptyDraft(nextTempId())]);
    };

    const handleRemoveVideo = (tempId) => {
        setDrafts((prev) => {
            const next = prev.filter((d) => d.tempId !== tempId);
            if (next.length === 0) {
                return [buildEmptyDraft(nextTempId())];
            }
            return next;
        });
    };

    const handleDragEnd = (result) => {
        if (!result.destination || result.destination.index === result.source.index) {
            return;
        }
        setDrafts((prev) => {
            const next = [...prev];
            const [moved] = next.splice(result.source.index, 1);
            next.splice(result.destination.index, 0, moved);
            return next;
        });
    };

    const rowFieldErrors = drafts.map(getRowFieldErrors);
    const hasPartialRow = rowFieldErrors.some(hasFieldError);
    const hasIncompleteRow = drafts.some(isRowContentIncomplete);
    const hasAnyContent = drafts.some((d) => (
        d.title.trim().length > 0 ||
        d.url.trim().length > 0 ||
        d.description.trim().length > 0
    ));
    const isClearingAll = initialVideos.length > 0 && !hasAnyContent;
    // Strict: SAVE only lights up when every row that has any content is
    // FULLY valid — required fields present, URL in the allowlist, title +
    // description within their length caps. A row that would fail server-side
    // must not be sent, because a mid-batch reject leaves the pre-failure
    // rows committed and Cancel offered as an undo it cannot perform (QA
    // reported this as the "partial save silently commits rows" bug).
    const isSavable = (hasAnyContent && !hasPartialRow && !hasIncompleteRow) || isClearingAll;

    const handleSave = async () => {
        if (!hasAnyContent && !isClearingAll) {
            // Nothing to save at all — treat the click as a nudge to fill
            // something in without lighting up an all-red error state.
            setHasAttemptedSave(true);
            return;
        }
        if (hasPartialRow) {
            setHasAttemptedSave(true);
            return;
        }
        setIsSaving(true);
        try {
            const kept = drafts.filter((d) => d.title.trim() && d.url.trim());
            const keptExistingIds = new Set(kept.filter((d) => d.id).map((d) => d.id));
            const toDelete = initialVideos.filter((v) => !keptExistingIds.has(v.id));

            const api = getApiAxiosInstance();

            await Promise.all(toDelete.map((v) => api.delete(`${RECRUITING_VIDEOS_ENDPOINT}/${v.id}`)));

            const trackedOps = [];

            const orderedIds = await kept.reduce(async (accPromise, draft) => {
                const acc = await accPromise;
                const payload = {
                    title: draft.title.trim(),
                    description: draft.description.trim(),
                    url: draft.url.trim(),
                };
                if (draft.id) {
                    const original = originalById.get(draft.id);
                    if (original && isDraftChanged(payload, original)) {
                        await api.put(`${RECRUITING_VIDEOS_ENDPOINT}/${draft.id}`, payload);
                        trackedOps.push({ kind: 'updated', payload });
                    }
                    acc.push(draft.id);
                    return acc;
                }
                const response = await api.post(RECRUITING_VIDEOS_ENDPOINT, payload);
                const newId = response.data?.id;
                if (newId) {
                    acc.push(newId);
                }
                trackedOps.push({ kind: 'added', payload });
                return acc;
            }, Promise.resolve([]));

            const initialExistingOrder = initialVideos
                .filter((v) => keptExistingIds.has(v.id))
                .map((v) => v.id);
            const finalExistingOrder = kept
                .filter((d) => d.id && keptExistingIds.has(d.id))
                .map((d) => d.id);
            const didReorder = initialExistingOrder.length >= 2 &&
                (
                    finalExistingOrder.length !== initialExistingOrder.length ||
                    finalExistingOrder.some((id, i) => id !== initialExistingOrder[i])
                );

            if (orderedIds.length > 1) {
                await api.put(RECRUITING_VIDEOS_ORDER_ENDPOINT, { orderedVideoIds: orderedIds });
            }

            trackedOps.forEach((op) => {
                if (op.kind === 'added') {
                    trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEO_ADDED, {
                        host: resolveVideoHost(op.payload.url),
                    });
                } else if (op.kind === 'updated') {
                    trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEO_UPDATED, {
                        host: resolveVideoHost(op.payload.url),
                    });
                }
            });
            toDelete.forEach(() => {
                trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEO_DELETED);
            });
            if (didReorder) {
                trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEOS_REORDERED, {
                    video_count: finalExistingOrder.length,
                });
            }

            // Successful save is a distinct flow from a user-initiated dismiss;
            // suppress the dismiss event that would otherwise fire when the
            // parent unmounts us in response to onSaved.
            hasFiredDismissEventRef.current = true;
            onSaved();
        } catch (err) {
            // Coach-visible copy — the string the modal actually surfaces via
            // showErrorPopup, matching AC "include the error message the coach
            // saw". Keep the raw exception message in a separate field for
            // debugging (network-error class, upstream detail).
            const coachVisibleMessage = 'We couldn\'t finish saving your videos. Some changes may not have been applied — please refresh and confirm your list.';
            trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEO_SAVE_FAILED, {
                error_message: coachVisibleMessage,
                raw_error: err?.message ?? 'unknown',
                status_code: err?.response?.status ?? null,
            });
            dispatch(showErrorPopup({
                message: coachVisibleMessage,
                error: err,
            }));
        } finally {
            setIsSaving(false);
        }
    };

    const dismissWithTracking = () => {
        if (!hasFiredDismissEventRef.current) {
            hasFiredDismissEventRef.current = true;
            const hasChanges = drafts.some((draft) => {
                if (!draft.id) {
                    return draft.title.trim().length > 0 ||
                        draft.description.trim().length > 0 ||
                        draft.url.trim().length > 0;
                }
                const original = originalById.get(draft.id);
                if (!original) {
                    return true;
                }
                return isDraftChanged({
                    title: draft.title.trim(),
                    description: draft.description.trim(),
                    url: draft.url.trim(),
                }, original);
            }) || drafts.length < initialVideos.length;
            trackPlayerEngagement(CppPlayerActions.CPP_PLAYER_VIDEOS_MODAL_DISMISSED, {
                had_changes: hasChanges,
            });
        }
        onClose();
    };

    const renderCard = (draft, index, dragProvided, fieldErrors) => (
        <div
            ref={dragProvided?.innerRef}
            {...(dragProvided?.draggableProps || {})}
            className={styles.videoCard}
            data-testid={`${TESTID_PREFIX}.card-${index}`}
        >
            <div className={styles.videoCardHeader}>
                {isReorderable && (
                    <span
                        {...(dragProvided?.dragHandleProps || {})}
                        className={styles.videoDragHandle}
                        aria-label="Reorder video"
                        data-testid={`${TESTID_PREFIX}.card-${index}.drag`}
                    >
                        <span className={styles.videoDragDot} />
                        <span className={styles.videoDragDot} />
                        <span className={styles.videoDragDot} />
                        <span className={styles.videoDragDot} />
                        <span className={styles.videoDragDot} />
                        <span className={styles.videoDragDot} />
                    </span>
                )}
                <span
                    className={styles.videoCardTitle}
                    data-testid={`${TESTID_PREFIX}.card-${index}.label`}
                >
                    Video {index + 1}
                </span>
                <button
                    type="button"
                    className={styles.videoCardCloseBtn}
                    onClick={() => handleRemoveVideo(draft.tempId)}
                    aria-label="Remove video"
                    data-testid={`${TESTID_PREFIX}.card-${index}.remove-btn`}
                >
                    <i className="fas fa-times" aria-hidden="true" />
                </button>
            </div>

            <div className={styles.videoField}>
                <div className={styles.videoFieldLabelRow}>
                    <label
                        className={styles.videoFieldLabel}
                        htmlFor={`recruiting-video-${draft.tempId}-title`}
                        data-testid={`${TESTID_PREFIX}.card-${index}.title-label`}
                    >
                        Title <span className={styles.videoFieldRequired}>(Required)</span>
                    </label>
                    <span
                        className={styles.videoFieldCounter}
                        data-testid={`${TESTID_PREFIX}.card-${index}.title-counter`}
                    >
                        {`${draft.title.length}/${TITLE_MAX_LENGTH}`}
                    </span>
                </div>
                <input
                    id={`recruiting-video-${draft.tempId}-title`}
                    type="text"
                    className={`form-control ${styles.videoFieldInput} ${fieldErrors?.title ? styles.videoFieldInputError : ''}`}
                    value={draft.title}
                    maxLength={TITLE_MAX_LENGTH}
                    onChange={(e) => updateField(draft.tempId, 'title', e.target.value)}
                    data-testid={`${TESTID_PREFIX}.card-${index}.title-input`}
                />
                {fieldErrors?.title && (
                    <div
                        className={styles.videoFieldError}
                        data-testid={`${TESTID_PREFIX}.card-${index}.title-error`}
                    >
                        {fieldErrors.title}
                    </div>
                )}
            </div>

            <div className={styles.videoField}>
                <div className={styles.videoFieldLabelRow}>
                    <label
                        className={styles.videoFieldLabel}
                        htmlFor={`recruiting-video-${draft.tempId}-description`}
                        data-testid={`${TESTID_PREFIX}.card-${index}.description-label`}
                    >
                        Description
                    </label>
                    <span
                        className={styles.videoFieldCounter}
                        data-testid={`${TESTID_PREFIX}.card-${index}.description-counter`}
                    >
                        {`${draft.description.length}/${DESCRIPTION_MAX_LENGTH}`}
                    </span>
                </div>
                <input
                    id={`recruiting-video-${draft.tempId}-description`}
                    type="text"
                    className={`form-control ${styles.videoFieldInput}`}
                    value={draft.description}
                    maxLength={DESCRIPTION_MAX_LENGTH}
                    onChange={(e) => updateField(draft.tempId, 'description', e.target.value)}
                    data-testid={`${TESTID_PREFIX}.card-${index}.description-input`}
                />
                {fieldErrors?.description && (
                    <div
                        className={styles.videoFieldError}
                        data-testid={`${TESTID_PREFIX}.card-${index}.description-error`}
                    >
                        {fieldErrors.description}
                    </div>
                )}
            </div>

            <div className={styles.videoField}>
                <div className={styles.videoFieldLabelRow}>
                    <label
                        className={styles.videoFieldLabel}
                        htmlFor={`recruiting-video-${draft.tempId}-url`}
                        data-testid={`${TESTID_PREFIX}.card-${index}.url-label`}
                    >
                        URL <span className={styles.videoFieldRequired}>(Required)</span>
                    </label>
                </div>
                <input
                    id={`recruiting-video-${draft.tempId}-url`}
                    type="url"
                    className={`form-control ${styles.videoFieldInput} ${fieldErrors?.url ? styles.videoFieldInputError : ''}`}
                    value={draft.url}
                    placeholder={URL_PLACEHOLDER}
                    onChange={(e) => updateField(draft.tempId, 'url', e.target.value)}
                    data-testid={`${TESTID_PREFIX}.card-${index}.url-input`}
                />
                {fieldErrors?.url && (
                    <div
                        className={styles.videoFieldError}
                        data-testid={`${TESTID_PREFIX}.card-${index}.url-error`}
                    >
                        {fieldErrors.url}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <PopupV2
            onCloseClick={dismissWithTracking}
            config={{
                size: 'medium',
                customHeader: () => null,
                contentClassName: styles.recruitingVideosPopupShell,
                bodyClassName: styles.recruitingVideosPopupBody,
            }}
        >
            <div
                className={styles.recruitingVideosModal}
                data-testid={`${TESTID_PREFIX}.container`}
            >
                <div className={styles.modalScrollable} ref={scrollableRef}>
                <div
                    className={styles.modalTitle}
                    data-testid={`${TESTID_PREFIX}.title`}
                >
                    Add up to 4 Videos for Coaches
                </div>

                {isReorderable ? (
                    <DragDropContext onDragEnd={handleDragEnd}>
                        <Droppable droppableId={DROPPABLE_ID}>
                            {(dropProvided) => (
                                <div
                                    ref={dropProvided.innerRef}
                                    {...dropProvided.droppableProps}
                                    className={styles.videoList}
                                    data-testid={`${TESTID_PREFIX}.list`}
                                >
                                    {drafts.map((draft, index) => (
                                        <Draggable key={draft.tempId} draggableId={draft.tempId} index={index}>
                                            {(dragProvided) => renderCard(draft, index, dragProvided, hasAttemptedSave
                                                    ? rowFieldErrors[index]
                                                    : getPreAttemptVisibleErrors(drafts[index], rowFieldErrors[index]))}
                                        </Draggable>
                                    ))}
                                    {dropProvided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                ) : (
                    <div
                        className={styles.videoList}
                        data-testid={`${TESTID_PREFIX}.list`}
                    >
                        {drafts.map((draft, index) => renderCard(draft, index, null, hasAttemptedSave
                                                    ? rowFieldErrors[index]
                                                    : getPreAttemptVisibleErrors(drafts[index], rowFieldErrors[index])))}
                    </div>
                )}

                {canAddMore && (
                    <div className={styles.addVideoBtnRow}>
                        <button
                            type="button"
                            className={styles.addVideoBtn}
                            onClick={handleAddVideo}
                            data-testid={`${TESTID_PREFIX}.add-video-btn`}
                        >
                            <svg
                                className={styles.addVideoBtnIcon}
                                viewBox="0 0 20 20"
                                aria-hidden="true"
                                focusable="false"
                            >
                                <circle cx="10" cy="10" r="8.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
                                <line x1="10" y1="6" x2="10" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                                <line x1="6" y1="10" x2="14" y2="10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                            </svg>
                            <span data-testid={`${TESTID_PREFIX}.add-video-btn.text`}>Add Video</span>
                        </button>
                    </div>
                )}
                </div>

                <div
                    className={styles.modalActions}
                    data-testid={`${TESTID_PREFIX}.actions`}
                >
                    <button
                        type="button"
                        className="btn btn-default btn-sm"
                        onClick={dismissWithTracking}
                        disabled={isSaving}
                        data-testid={`${TESTID_PREFIX}.cancel-btn`}
                    >
                        CANCEL
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary btn-sm"
                        onClick={handleSave}
                        // Hard-disable only while a save is in flight — for the
                        // "row is incomplete" case we leave the button clickable
                        // so it can trip hasAttemptedSave and show errors; the
                        // style below makes it read as dim.
                        disabled={isSaving}
                        aria-disabled={!isSavable || isSaving}
                        style={!isSavable && !isSaving ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                        data-testid={`${TESTID_PREFIX}.save-btn`}
                    >
                        SAVE
                    </button>
                </div>
            </div>
        </PopupV2>
    );
};

RecruitingVideosModal.propTypes = {
    initialVideos: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string,
        description: PropTypes.string,
        url: PropTypes.string,
    })),
    onClose: PropTypes.func.isRequired,
    onSaved: PropTypes.func.isRequired,
};

RecruitingVideosModal.defaultProps = {
    initialVideos: [],
};

export default RecruitingVideosModal;
